import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';

import Button from '../../../components/ui/Button';
import { storageService } from '../../../services/storageService';
import { supabase } from '../../../lib/supabase';

const HotspotConfigPanel = ({ hotspot, onUpdate, onDelete, onClose, onCopy }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: hotspot?.type || 'link',
    url: hotspot?.url || '',
    sku: hotspot?.sku || '',
    quantity: hotspot?.quantity || 1,
    seller: hotspot?.seller || '',
    vtexStoreUrl: hotspot?.vtexStoreUrl || '',
    title: hotspot?.title || '',
    description: hotspot?.description || '',
    buttonText: hotspot?.buttonText || 'Learn More',
    buttonColor: hotspot?.buttonColor || '#D69E2E',
    imageUrl: hotspot?.imageUrl || '',
    videoUrl: hotspot?.videoUrl || '',
    internalLinkPage: hotspot?.internalLinkPage || null,
    width: hotspot?.width || 200,
    height: hotspot?.height || 200,
    x: hotspot?.x || 100,
    y: hotspot?.y || 100
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (hotspot) {
      setFormData({
        type: hotspot?.type || 'link',
        url: hotspot?.url || '',
        sku: hotspot?.sku || '',
        quantity: hotspot?.quantity || 1,
        seller: hotspot?.seller || '',
        vtexStoreUrl: hotspot?.vtexStoreUrl || '',
        title: hotspot?.title || '',
        description: hotspot?.description || '',
        buttonText: hotspot?.buttonText || 'Learn More',
        buttonColor: hotspot?.buttonColor || '#D69E2E',
        imageUrl: hotspot?.imageUrl || '',
        videoUrl: hotspot?.videoUrl || '',
        internalLinkPage: hotspot?.internalLinkPage || null,
        width: hotspot?.width || 200,
        height: hotspot?.height || 200,
        x: hotspot?.x || 100,
        y: hotspot?.y || 100
      });
    }
  }, [hotspot]);

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload to catalog-thumbnails bucket (public)
      const { publicUrl } = await storageService?.uploadThumbnail(file);
      setFormData({ ...formData, imageUrl: publicUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(t('catalogEditor.hotspotConfig.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!hotspot) return;

    try {
      // Validate required fields based on type
      if (hotspot?.type === 'link' && !formData?.url) {
        alert(t('catalogEditor.hotspotConfig.validationUrl'));
        return;
      }

      if (hotspot?.type === 'buy' && !formData?.sku) {
        alert(t('catalogEditor.hotspotConfig.validationSku'));
        return;
      }

      if (hotspot?.type === 'image' && !formData?.imageUrl) {
        alert(t('catalogEditor.hotspotConfig.validationImage'));
        return;
      }

      if (hotspot?.type === 'video' && !formData?.videoUrl) {
        alert(t('catalogEditor.hotspotConfig.validationVideo'));
        return;
      }

      if (hotspot?.type === 'internal_link') {
        if (!formData?.internalLinkPage) {
          alert(t('catalogEditor.hotspotConfig.validationDestPage'));
          return;
        }
        if (formData?.internalLinkPage === hotspot?.pageNumber) {
          alert(t('catalogEditor.hotspotConfig.validationSamePage'));
          return;
        }
      }

      await onUpdate(hotspot?.id, formData);
    } catch (error) {
      console.error('Error saving hotspot:', error);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData?.type) {
      case 'link':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.urlLabel')} *
              </label>
              <Input
                type="url"
                value={formData?.url || ''}
                onChange={(e) => setFormData({ ...formData, url: e?.target?.value })}
                placeholder="https://example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.buttonText')}
              </label>
              <Input
                type="text"
                value={formData?.buttonText || ''}
                onChange={(e) => setFormData({ ...formData, buttonText: e?.target?.value })}
                placeholder="Learn More"
              />
            </div>
          </div>
        );

      case 'buy':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.skuLabel')} *
              </label>
              <Input
                type="text"
                value={formData?.sku || ''}
                onChange={(e) => setFormData({ ...formData, sku: e?.target?.value })}
                placeholder="12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.buttonText')}
              </label>
              <Input
                type="text"
                value={formData?.buttonText || ''}
                onChange={(e) => setFormData({ ...formData, buttonText: e?.target?.value })}
                placeholder="Buy Now"
              />
            </div>
          </div>
        );

      case 'info':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.infoTitle')}
              </label>
              <Input
                type="text"
                value={formData?.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                placeholder="Information Title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.infoDesc')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows={4}
                value={formData?.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                placeholder="Additional information..."
              />
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.uploadImage')} *
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-accent-foreground hover:file:bg-accent/90"
              />
              {uploading && <p className="text-sm text-muted-foreground mt-2">{t('catalogEditor.hotspotConfig.uploading')}</p>}
              {formData?.imageUrl && (
                <div className="mt-3">
                  <img
                    src={formData?.imageUrl}
                    alt="Uploaded preview"
                    className="w-full h-32 object-cover rounded-md border border-border"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('catalogEditor.hotspotConfig.widthPx')}
                </label>
                <Input
                  type="number"
                  min="50"
                  max="2000"
                  value={formData?.width || 200}
                  onChange={(e) => setFormData({ ...formData, width: parseFloat(e?.target?.value) })}
                  placeholder="200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('catalogEditor.hotspotConfig.heightPx')}
                </label>
                <Input
                  type="number"
                  min="50"
                  max="2000"
                  value={formData?.height || 200}
                  onChange={(e) => setFormData({ ...formData, height: parseFloat(e?.target?.value) })}
                  placeholder="200"
                />
              </div>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.videoUrl')} *
              </label>
              <Input
                type="url"
                value={formData?.videoUrl || ''}
                onChange={(e) => setFormData({ ...formData, videoUrl: e?.target?.value })}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('catalogEditor.hotspotConfig.videoHint')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('catalogEditor.hotspotConfig.widthPx')}
                </label>
                <Input
                  type="number"
                  min="50"
                  max="2000"
                  value={formData?.width || 200}
                  onChange={(e) => setFormData({ ...formData, width: parseFloat(e?.target?.value) })}
                  placeholder="200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('catalogEditor.hotspotConfig.heightPx')}
                </label>
                <Input
                  type="number"
                  min="50"
                  max="2000"
                  value={formData?.height || 200}
                  onChange={(e) => setFormData({ ...formData, height: parseFloat(e?.target?.value) })}
                  placeholder="200"
                />
              </div>
            </div>
          </div>
        );

      case 'internal_link':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.internalTitle')} <span className="text-muted-foreground font-normal">({t('catalogEditor.hotspotConfig.internalTitleOptional')})</span>
              </label>
              <Input
                type="text"
                value={formData?.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                placeholder={t('catalogEditor.hotspotConfig.internalTitlePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('catalogEditor.hotspotConfig.destPage')} *
              </label>
              <select
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                value={formData?.internalLinkPage || ''}
                onChange={(e) => setFormData({ ...formData, internalLinkPage: parseInt(e?.target?.value) })}
              >
                <option value="">{t('catalogEditor.hotspotConfig.destPagePlaceholder')}</option>
                {hotspot?.catalogPages?.map((page) => (
                  <option
                    key={page?.pageNumber}
                    value={page?.pageNumber}
                    disabled={page?.pageNumber === hotspot?.pageNumber}
                  >
                    {t('catalogEditor.hotspotList.pageLabel', { number: page?.pageNumber })} {page?.pageNumber === hotspot?.pageNumber ? `(${t('catalogEditor.hotspotConfig.destPageCurrent')})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {t('catalogEditor.hotspotConfig.destPageHint')}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border-l border-border h-full overflow-y-auto">
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">{t('catalogEditor.hotspotConfig.settingsTitle')}</h3>
          {/*
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
          */}
        </div>

        {/* Hotspot Type */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('catalogEditor.hotspotConfig.settingsTitle')}
          </label>
          <select
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            value={formData?.type || 'link'}
            onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
          >
            <option value="link">{t('catalogEditor.hotspotConfig.typeLink')}</option>
            <option value="buy">{t('catalogEditor.hotspotConfig.typeBuy')}</option>
            <option value="info">{t('catalogEditor.hotspotConfig.typeInfo')}</option>
            <option value="image">{t('catalogEditor.hotspotConfig.typeImage')}</option>
            <option value="video">{t('catalogEditor.hotspotConfig.typeVideo')}</option>
            <option value="internal_link">{t('catalogEditor.hotspotConfig.typeInternalLink')}</option>
          </select>
        </div>

        {/* Type-specific fields */}
        {renderTypeSpecificFields()}

        {/* Dimensions - Show for all hotspot types except image, video, and internal_link */}
        {formData?.type !== 'image' && formData?.type !== 'video' && formData?.type !== 'internal_link' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('catalogEditor.hotspotConfig.dimensions')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('catalogEditor.hotspotConfig.widthPx')}
                </label>
                <Input
                  type="number"
                  min="20"
                  max="1200"
                  value={formData?.width || 200}
                  onChange={(e) => setFormData({ ...formData, width: parseInt(e?.target?.value) || 200 })}
                  placeholder="200"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('catalogEditor.hotspotConfig.heightPx')}
                </label>
                <Input
                  type="number"
                  min="20"
                  max="1800"
                  value={formData?.height || 60}
                  onChange={(e) => setFormData({ ...formData, height: parseInt(e?.target?.value) || 60 })}
                  placeholder="60"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('catalogEditor.hotspotConfig.dimensionsHint')}
            </p>
          </div>
        )}

        {/* Position Controls */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('catalogEditor.hotspotConfig.position')}
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                {t('catalogEditor.hotspotConfig.positionX')}
              </label>
              <Input
                type="number"
                min="0"
                value={formData?.x || 0}
                onChange={(e) => setFormData({ ...formData, x: parseInt(e?.target?.value) || 0 })}
                placeholder="100"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                {t('catalogEditor.hotspotConfig.positionY')}
              </label>
              <Input
                type="number"
                min="0"
                value={formData?.y || 0}
                onChange={(e) => setFormData({ ...formData, y: parseInt(e?.target?.value) || 0 })}
                placeholder="100"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {t('catalogEditor.hotspotConfig.positionHint')}
          </p>
        </div>

        {/* Common fields */}
        {(formData?.type === 'link' || formData?.type === 'buy' || formData?.type === 'info') && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('catalogEditor.hotspotConfig.buttonColor')}
            </label>
            <Input
              type="color"
              value={formData?.buttonColor || (formData?.type === 'info' ? '#7C3AED' : '#D69E2E')}
              onChange={(e) => setFormData({ ...formData, buttonColor: e?.target?.value })}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="default"
            className="flex-1"
            onClick={handleSave}
            iconName="Save"
            iconPosition="left"
          >
            {t('catalogEditor.hotspotConfig.saveChanges')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onCopy?.(hotspot)}
            iconName="Copy"
            title={t('catalogEditor.hotspotConfig.copyToPage')}
          />
          <Button
            variant="destructive"
            onClick={() => onDelete(hotspot?.id)}
            iconName="Trash2"
          />
        </div>
      </div>
    </div>
  );
};

export default HotspotConfigPanel;